import { SurveyScopeEntityType, SurveyScopeExclusion } from './surveyScopeExclusionTableSchema';

export interface IScopeAncestry {
    elementCategoryId: string | null;
    elementId: string | null;
    unitId: string | null;
    manifestationId: string | null;
}

// An entity is in scope unless either itself or one of its ancestors
// appears in the exclusions list. Exclusions are stored only at the
// top-most excluded node per path — see migration 000050 and
// wiki/02-Software/Scope-feature.md.
//
// `ancestry` lists the ids the caller has already resolved for the entity
// being checked, with deeper levels nullable. For a manifestation, all four
// may be present; for an element, only elementCategoryId + elementId; etc.
export function isInScope(exclusions: SurveyScopeExclusion[], ancestry: IScopeAncestry): boolean {
    const matchers: Array<[SurveyScopeEntityType, string | null]> = [
        ['elementCategory', ancestry.elementCategoryId],
        ['element', ancestry.elementId],
        ['unit', ancestry.unitId],
        ['manifestation', ancestry.manifestationId],
    ];

    for (const exclusion of exclusions) {
        for (const [type, id] of matchers) {
            if (id && exclusion.entityType === type && exclusion.entityId === id) {
                return false;
            }
        }
    }

    return true;
}

// Reduce the user's selection of excluded items to the minimal set we
// persist: drop any exclusion whose ancestor is already excluded.
// The frontend gives us the full set the user wants disabled; we keep
// only the upper-most node per path.
export function minimizeExclusions(
    exclusions: Array<{ entityType: SurveyScopeEntityType; entityId: string }>,
    ancestorLookup: (entityType: SurveyScopeEntityType, entityId: string) => IScopeAncestry,
): Array<{ entityType: SurveyScopeEntityType; entityId: string }> {
    const indexed = new Map<string, { entityType: SurveyScopeEntityType; entityId: string }>();
    for (const exclusion of exclusions) {
        indexed.set(`${exclusion.entityType}:${exclusion.entityId}`, exclusion);
    }

    const result: Array<{ entityType: SurveyScopeEntityType; entityId: string }> = [];
    for (const exclusion of exclusions) {
        const ancestry = ancestorLookup(exclusion.entityType, exclusion.entityId);
        let isCoveredByAncestor = false;
        const ancestorIds: Array<[SurveyScopeEntityType, string | null]> = [
            ['elementCategory', ancestry.elementCategoryId],
            ['element', ancestry.elementId],
            ['unit', ancestry.unitId],
        ];
        for (const [type, id] of ancestorIds) {
            if (id && type !== exclusion.entityType && indexed.has(`${type}:${id}`)) {
                isCoveredByAncestor = true;
                break;
            }
        }
        if (!isCoveredByAncestor) {
            result.push(exclusion);
        }
    }
    return result;
}
